import { useContext } from 'react'
import type {
  TypeArticleWithoutUnresolvableLinksResponse,
  TypeCategoryWithoutUnresolvableLinksResponse,
} from '@/bb-contentful-types'
import { FlexGrid, Feedback } from '@telus-uds/components-web'
import { articleConsumer } from '../../../provider/ArticleProvider'
import ShowMoreButton from '../../ShowMoreButton'
import Articles from '../Articles'
import SkeletonViewArticles from '../SkeletonViewArticles'

import {
  PreviewContainerWhiteBackground,
  PreviewContainerGrayBackground,
  HeadingContainer,
  BottomNavigationContainer,
} from '../styles'
import Heading from '@/siteBuilder/blocks/core/Heading/Heading'
import { useFetchArticles, useFetchArticlesInfinity } from '@/hooks/useFetchArticles'
import { flattenData } from '../utils/flattenData'
import { findArticleIds } from '../utils/findArticleIds'
import STATIC_CONTENTS from '../../../utils/staticContent'
import type { Items } from '../../../utils/types/items'
import type { Language } from '@/siteBuilder/locale'
import type { BlogUrl } from 'src/blogBuilder/utils/blogUrl'
import { filterArticleTags } from '../../../utils/filterArticleTags'

export type Props = {
  blogs: TypeCategoryWithoutUnresolvableLinksResponse[]
  lang: Language
  fullBlogUrls: BlogUrl
  theme: 'blog' | 'story'
}

const ShowMorePreview = ({ blogs, lang, fullBlogUrls, theme }: Props) => {
  const { articles, selectedCategory, count, allArticles, articlesPerPage, selectedTags } = useContext(articleConsumer)

  const PreviewContainer = theme === 'story' ? PreviewContainerWhiteBackground : PreviewContainerGrayBackground

  const filteredArticles = filterArticleTags(allArticles, selectedTags)

  const infiniteArticles: TypeArticleWithoutUnresolvableLinksResponse[] = filteredArticles.slice(
    count,
    count + articlesPerPage
  )
  const articleIds = findArticleIds(blogs, infiniteArticles, selectedCategory)
  const articleIdsInitial = findArticleIds(blogs, selectedTags.length ? filteredArticles : articles, selectedCategory)
  // this will load the initial 12 articles
  const { data: initialData, isLoading: initialLoad, isError: initialError } = useFetchArticles(articleIdsInitial, lang)

  // this will load articles after they click, "show more"
  const { data, isLoading, isError, size, setSize } = useFetchArticlesInfinity(
    articleIds.join(),
    selectedCategory,
    selectedTags,
    lang
  )

  if (isError || initialError || initialData?.sys?.type === 'Error') {
    return (
      <Feedback validation="error" variant={{ icon: true }} title="Sorry">
        Something went wrong...
      </Feedback>
    )
  }

  const items: Items[] = data
    ? data.flatMap((d) => {
        return {
          items: d.items,
          includes: d.includes,
        }
      })
    : []

  items.unshift(initialData)

  return (
    <>
      <PreviewContainer data-testid="bb-story-preview">
        <FlexGrid>
          <FlexGrid.Row>
            {(isLoading || initialLoad) && <SkeletonViewArticles count={count} theme={theme} />}
            {items && (
              <Articles
                theme={theme}
                fullBlogUrls={fullBlogUrls}
                listOfArticles={flattenData(items)}
                lang={lang}
                selectedCategory={selectedCategory}
                displayCategory={selectedCategory === STATIC_CONTENTS.ALL[lang]}
              />
            )}
          </FlexGrid.Row>
          {/* Show more button */}
          <FlexGrid.Row horizontalAlign="center">
            <FlexGrid.Col xs={12}>
              <BottomNavigationContainer>
                <ShowMoreButton size={size} setSize={setSize} lang={lang} countIncrease={articlesPerPage} />
              </BottomNavigationContainer>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </PreviewContainer>
    </>
  )
}

export default ShowMorePreview
